import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { plansApiService } from '../api/plans-api';
import type { MotivoDeCancelacionAdmin } from '../api/motivo-de-cancelacion';
import {
  Plan,
  Subscription,
  CreatePlanDto,
  UpdatePlanDto,
  CreateSubscriptionDto,
  UpdateSubscriptionDto,
  PlansState,
  PlanFilters,
  SubscriptionFilters,
} from '../types/admin.types';

// Función para calcular estadísticas basadas en los planes
const calculateStats = (plans: Plan[]) => {
  const stats = {
    total: plans.length,
    active: plans.filter(plan => plan.status === 'active').length,
    inactive: plans.filter(plan => plan.status === 'inactive').length,
    draft: plans.filter(plan => plan.status === 'draft').length,
    public: plans.filter(plan => plan.visibility === 'public').length,
    private: plans.filter(plan => plan.visibility === 'private').length,
  };
  
  return stats;
};

// Estado inicial
const initialPlansState: PlansState = {
  plans: [],
  subscriptions: [],
  currentPlan: null,
  currentSubscription: null,
  loading: false,
  error: null,
  updating: [],
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
  filters: {},
  stats: {
    total: 0,
    active: 0,
    inactive: 0,
    draft: 0,
    public: 0,
    private: 0,
  },
};

// Async Thunks para Planes
export const fetchPlans = createAsyncThunk(
  'plans/fetchPlans',
  async (filters: PlanFilters = {}, { rejectWithValue }) => {
    try {
      const response = await plansApiService.getAllPlans(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar planes'
      );
    }
  }
);

export const fetchPlan = createAsyncThunk(
  'plans/fetchPlan',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await plansApiService.getPlan(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar plan'
      );
    }
  }
);

export const createPlan = createAsyncThunk(
  'plans/createPlan',
  async (data: CreatePlanDto, { rejectWithValue }) => {
    try {
      const response = await plansApiService.createPlan(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear plan'
      );
    }
  }
);

export const updatePlan = createAsyncThunk(
  'plans/updatePlan',
  async (
    { id, data }: { id: string; data: UpdatePlanDto },
    { rejectWithValue }
  ) => {
    try {
      const response = await plansApiService.updatePlan(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar plan'
      );
    }
  }
);

export const fetchPlanById = createAsyncThunk(
  'plans/fetchPlanById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await plansApiService.getPlanById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al obtener plan'
      );
    }
  }
);

export const deletePlan = createAsyncThunk(
  'plans/deletePlan',
  async (id: string, { rejectWithValue }) => {
    try {
      await plansApiService.deletePlan(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar plan'
      );
    }
  }
);

export const togglePlanStatus = createAsyncThunk(
  'plans/togglePlanStatus',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await plansApiService.togglePlanStatus(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cambiar estado del plan'
      );
    }
  }
);

export const duplicatePlan = createAsyncThunk(
  'plans/duplicatePlan',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await plansApiService.duplicatePlan(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al duplicar plan'
      );
    }
  }
);

export const fetchPlanStats = createAsyncThunk(
  'plans/fetchPlanStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await plansApiService.getPlanStats();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar estadísticas'
      );
    }
  }
);

// Async Thunks para Suscripciones
export const fetchSubscriptions = createAsyncThunk(
  'plans/fetchSubscriptions',
  async (filters: SubscriptionFilters = {}, { rejectWithValue }) => {
    try {
      const response = await plansApiService.subscriptions.getSubscriptions(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar suscripciones'
      );
    }
  }
);

export const fetchSubscription = createAsyncThunk(
  'plans/fetchSubscription',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await plansApiService.subscriptions.getSubscription(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar suscripción'
      );
    }
  }
);

export const createSubscription = createAsyncThunk(
  'plans/createSubscription',
  async (data: CreateSubscriptionDto, { rejectWithValue }) => {
    try {
      const response = await plansApiService.subscriptions.createSubscription(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear suscripción'
      );
    }
  }
);

export const updateSubscription = createAsyncThunk(
  'plans/updateSubscription',
  async (
    { id, data }: { id: string; data: UpdateSubscriptionDto },
    { rejectWithValue }
  ) => {
    try {
      const response = await plansApiService.subscriptions.updateSubscription(
        id,
        data
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar suscripción'
      );
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  'plans/cancelSubscription',
  async (
    { id, motivo, reason }: { id: string; motivo: MotivoDeCancelacionAdmin; reason?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await plansApiService.subscriptions.cancelSubscription(
        id,
        motivo,
        reason
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cancelar suscripción'
      );
    }
  }
);

export const renewSubscription = createAsyncThunk(
  'plans/renewSubscription',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await plansApiService.subscriptions.renewSubscription(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al renovar suscripción'
      );
    }
  }
);

export const deleteSubscription = createAsyncThunk(
  'plans/deleteSubscription',
  async (id: string, { rejectWithValue }) => {
    try {
      await plansApiService.subscriptions.deleteSubscription(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar suscripción'
      );
    }
  }
);

// Slice
const plansSlice = createSlice({
  name: 'plans',
  initialState: initialPlansState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    setCurrentPlan: (state, action: PayloadAction<Plan | null>) => {
      state.currentPlan = action.payload;
    },
    setCurrentSubscription: (
      state,
      action: PayloadAction<Subscription | null>
    ) => {
      state.currentSubscription = action.payload;
    },
    setFilters: (state, action: PayloadAction<PlanFilters>) => {
      state.filters = action.payload;
    },
    setPagination: (
      state,
      action: PayloadAction<{ page: number; limit: number; total: number }>
    ) => {
      state.pagination = action.payload;
    },
    clearPlans: state => {
      state.plans = [];
      state.currentPlan = null;
    },
    clearSubscriptions: state => {
      state.subscriptions = [];
      state.currentSubscription = null;
    },
  },
  extraReducers: builder => {
    // Planes
    builder
      .addCase(fetchPlans.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.loading = false;
        // Extraer el array de datos si viene en un wrapper
        const plansData = (action.payload as any)?.data || action.payload;
        state.plans = Array.isArray(plansData) ? plansData : [];
        state.pagination.total = state.plans.length;
        // Calcular estadísticas basadas en los planes cargados
        state.stats = calculateStats(state.plans);
      })
      .addCase(fetchPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchPlan.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlan.fulfilled, (state, action) => {
        state.loading = false;
        // Extraer el plan si viene en un wrapper
        const planData = (action.payload as any)?.data || action.payload;
        state.currentPlan = planData;
      })
      .addCase(fetchPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createPlan.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPlan.fulfilled, (state, action) => {
        state.loading = false;
        // Extraer el plan si viene en un wrapper
        const planData = (action.payload as any)?.data || action.payload;
        // Asegurar que state.plans sea un array
        if (!Array.isArray(state.plans)) {
          state.plans = [];
        }
        state.plans.push(planData);
        state.pagination.total += 1;
        // Recalcular estadísticas
        state.stats = calculateStats(state.plans);
      })
      .addCase(createPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updatePlan.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePlan.fulfilled, (state, action) => {
        state.loading = false;
        // Extraer el plan si viene en un wrapper
        const planData = (action.payload as any)?.data || action.payload;
        // Asegurar que state.plans sea un array
        if (!Array.isArray(state.plans)) {
          state.plans = [];
        }
        const index = state.plans.findIndex(
          plan => plan.id === planData.id
        );

        if (index !== -1) {
          state.plans[index] = planData;
        }
        if (state.currentPlan?.id === planData.id) {
          state.currentPlan = planData;
        }
        // Recalcular estadísticas
        state.stats = calculateStats(state.plans);
      })
      .addCase(updatePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchPlanById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlanById.fulfilled, (state, action) => {
        state.loading = false;
        // Extraer el plan si viene en un wrapper
        const planData = (action.payload as any)?.data || action.payload;
        // Asegurar que state.plans sea un array
        if (!Array.isArray(state.plans)) {
          state.plans = [];
        }
        const index = state.plans.findIndex(
          plan => plan.id === planData.id
        );

        if (index !== -1) {
          state.plans[index] = planData;
        } else {
          state.plans.push(planData);
        }
      })
      .addCase(fetchPlanById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deletePlan.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePlan.fulfilled, (state, action) => {
        state.loading = false;
        // Asegurar que state.plans sea un array
        if (!Array.isArray(state.plans)) {
          state.plans = [];
        }
        state.plans = state.plans.filter(plan => plan.id !== action.payload);
        state.pagination.total -= 1;
        if (state.currentPlan?.id === action.payload) {
          state.currentPlan = null;
        }
        // Recalcular estadísticas
        state.stats = calculateStats(state.plans);
      })
      .addCase(deletePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(togglePlanStatus.pending, (state, action) => {
        const planId = action.meta.arg;
        if (!state.updating.includes(planId)) {
          state.updating.push(planId);
        }
        state.error = null;
      })
      .addCase(togglePlanStatus.fulfilled, (state, action) => {
        const planId = action.meta.arg;
        state.updating = state.updating.filter(id => id !== planId);

        // Extraer el plan si viene en un wrapper
        const planData = (action.payload as any)?.data || action.payload;
        // Asegurar que state.plans sea un array
        if (!Array.isArray(state.plans)) {
          state.plans = [];
        }
        const index = state.plans.findIndex(
          plan => plan.id === planData.id
        );

        if (index !== -1) {
          state.plans[index] = planData;
        }
        if (state.currentPlan?.id === planData.id) {
          state.currentPlan = planData;
        }
        // Recalcular estadísticas
        state.stats = calculateStats(state.plans);
      })
      .addCase(togglePlanStatus.rejected, (state, action) => {
        const planId = action.meta.arg;
        state.updating = state.updating.filter(id => id !== planId);
        state.error = action.payload as string;
      })
      .addCase(duplicatePlan.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(duplicatePlan.fulfilled, (state, action) => {
        state.loading = false;
        // Extraer el plan si viene en un wrapper
        const planData = (action.payload as any)?.data || action.payload;
        // Asegurar que state.plans sea un array
        if (!Array.isArray(state.plans)) {
          state.plans = [];
        }
        state.plans.push(planData);
        state.pagination.total += 1;
      })
      .addCase(duplicatePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchPlanStats.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlanStats.fulfilled, (state, action) => {
        state.loading = false;
        // Extraer las stats si vienen en un wrapper
        const statsData = (action.payload as any)?.data || action.payload;
        state.stats = statsData;
      })
      .addCase(fetchPlanStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Suscripciones
      .addCase(fetchSubscriptions.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        const data = (action.payload as any)?.data || action.payload;
        state.subscriptions = Array.isArray(data) ? data : [];
      })
      .addCase(fetchSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchSubscription.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscription.fulfilled, (state, action) => {
        state.loading = false;
        const data = (action.payload as any)?.data || action.payload;
        state.currentSubscription = data;
      })
      .addCase(fetchSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createSubscription.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSubscription.fulfilled, (state, action) => {
        state.loading = false;
        const data = (action.payload as any)?.data || action.payload;
        state.subscriptions.push(data);
      })
      .addCase(createSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateSubscription.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSubscription.fulfilled, (state, action) => {
        state.loading = false;
        const data = (action.payload as any)?.data || action.payload;
        const index = state.subscriptions.findIndex(
          sub => sub.id === data.id
        );
        if (index !== -1) {
          state.subscriptions[index] = data;
        }
        if (state.currentSubscription?.id === data.id) {
          state.currentSubscription = data;
        }
      })
      .addCase(updateSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(cancelSubscription.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.loading = false;
        const data = (action.payload as any)?.data || action.payload;
        const index = state.subscriptions.findIndex(
          sub => sub.id === data.id
        );
        if (index !== -1) {
          state.subscriptions[index] = data;
        }
        if (state.currentSubscription?.id === data.id) {
          state.currentSubscription = data;
        }
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(renewSubscription.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(renewSubscription.fulfilled, (state, action) => {
        state.loading = false;
        const data = (action.payload as any)?.data || action.payload;
        const index = state.subscriptions.findIndex(
          sub => sub.id === data.id
        );
        if (index !== -1) {
          state.subscriptions[index] = data;
        }
        if (state.currentSubscription?.id === data.id) {
          state.currentSubscription = data;
        }
      })
      .addCase(renewSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteSubscription.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = state.subscriptions.filter(
          sub => sub.id !== action.payload
        );
        if (state.currentSubscription?.id === action.payload) {
          state.currentSubscription = null;
        }
      })
      .addCase(deleteSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setCurrentPlan,
  setCurrentSubscription,
  setFilters,
  setPagination,
  clearPlans,
  clearSubscriptions,
} = plansSlice.actions;

export default plansSlice.reducer;
